import axiosInstance from "../redux/api";
import { toast } from "../Hooks/UseToast";

const PAGINATION_KEYS = new Set(["page", "per_page"]);

const sanitizeFilters = (params = {}) => {
  const out = {};
  Object.entries(params).forEach(([key, value]) => {
    if (PAGINATION_KEYS.has(key)) return;
    if (value === undefined || value === null) return;
    out[key] = value;
  });
  return out;
};

const filenameFromContentDisposition = (header) => {
  if (!header) return null;
  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(header);
  if (utf8?.[1]) {
    try {
      return decodeURIComponent(utf8[1].replace(/^"|"$/g, ""));
    } catch {
      return utf8[1].replace(/^"|"$/g, "");
    }
  }
  const ascii = /filename="?([^";]+)"?/i.exec(header);
  return ascii?.[1] || null;
};

const triggerBrowserDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const readBlobAsText = (blob) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result || "");
    reader.onerror = () => resolve("");
    reader.readAsText(blob);
  });

const SUPPORTED_FORMATS = new Set(["csv", "xlsx"]);

const extensionForFormat = (format) => (format === "xlsx" ? "xlsx" : "csv");

const parseErrorDescription = async (error, fallback) => {
  const blob = error?.response?.data;
  if (!blob || typeof blob.text !== "function") return fallback;
  const text = await readBlobAsText(blob);
  try {
    const parsed = JSON.parse(text);
    if (parsed?.message) return parsed.message;
  } catch {
    // Non-JSON payload — keep the fallback.
  }
  return fallback;
};

export const exportResource = async (resource, params = {}, format = "csv") => {
  const normalizedFormat = SUPPORTED_FORMATS.has(format) ? format : "csv";
  const queryParams = sanitizeFilters(params);
  // Omit `format` for CSV so legacy behavior is byte-identical to the pre-XLSX API.
  if (normalizedFormat !== "csv") queryParams.format = normalizedFormat;

  try {
    const response = await axiosInstance.get(`admin/exports/${resource}`, {
      params: queryParams,
      responseType: "blob",
    });

    const filename =
      filenameFromContentDisposition(response.headers?.["content-disposition"]) ||
      `${resource}_${Date.now()}.${extensionForFormat(normalizedFormat)}`;

    triggerBrowserDownload(response.data, filename);
    return { ok: true };
  } catch (error) {
    const status = error?.response?.status;

    if (status === 403) {
      toast({
        title: "Export not allowed",
        description: "You do not have permission to export this data.",
        variant: "destructive",
      });
      return { ok: false, status };
    }

    const description = await parseErrorDescription(
      error,
      "Could not generate the export. Please try again."
    );
    toast({
      title: "Export failed",
      description,
      variant: "destructive",
    });
    return { ok: false, status };
  }
};

export const downloadOrderInvoice = async (orderId) => {
  if (!orderId) return { ok: false, status: 0 };
  try {
    const response = await axiosInstance.get(
      `admin/orders/${orderId}/invoice.pdf`,
      { responseType: "blob" }
    );

    const filename =
      filenameFromContentDisposition(response.headers?.["content-disposition"]) ||
      `invoice_${orderId}.pdf`;

    triggerBrowserDownload(response.data, filename);
    return { ok: true };
  } catch (error) {
    const status = error?.response?.status;

    if (status === 403) {
      toast({
        title: "Not allowed",
        description: "You do not have permission to download this invoice.",
        variant: "destructive",
      });
      return { ok: false, status };
    }
    if (status === 404) {
      toast({
        title: "Invoice not found",
        description: "The order could not be found.",
        variant: "destructive",
      });
      return { ok: false, status };
    }

    const description = await parseErrorDescription(
      error,
      "Failed to generate invoice. Please try again or contact support."
    );
    toast({
      title: "Invoice download failed",
      description,
      variant: "destructive",
    });
    return { ok: false, status };
  }
};
